import React from 'react';
import styled from 'styled-components';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { toScreen } from '@doltech/core/lib/responsive/responsive.util';
import { Rating } from './Rating';
import { motion } from 'framer-motion';
import {
  Tag1,
  tag1Variants,
  Tag2,
  tag2Variants,
  Tag3,
  tag3Variants,
  tagWrapperVariants,
} from '../animations/reviewTags';
import {
  CertId,
  CertUs,
  CertVn,
  certWrapperVariants,
  certIdVariants,
  certUsVariants,
  certVnVariants,
} from '../animations/certificates';
import { NationId, NationUs, NationVn } from './Nations';
import {
  Book1,
  Book2,
  Book3,
  book1Variants,
  book2Variants,
  book3Variants,
  bookWrapperVariants,
} from '../animations/books';
import { NewTabLink } from './NewTabLink';
import { hext2Rgba } from '@doltech/utils/lib/color';
import { LazyImage } from '@doltech/ui/lib/figma/Image/LazyImage';
import { getAssetLinkByHandle } from '@doltech/utils/lib/url';
import { environment } from '@doltech/core/lib/environment/environment';

const AnimationWrapper = styled.div`
  position: absolute;
  z-index: 0;
  width: 136px;
  height: 121px;
  bottom: 14px;
  right: 0;
  overflow: hidden;
  pointer-events: none;
`;

const Container = styled.div<{ $topHeight: number }>`
  display: flex;
  flex-direction: column;
  gap: 32px;
  position: fixed;
  top: ${(props) => props.$topHeight}%;
  right: 0;
  transform: translateY(-75%);
  z-index: 105;

  ${toScreen(1143)} {
    display: none;
  }
`;

const CtaCardWrapper = styled(motion.div)<{ $color: string }>`
  background-color: ${colorsV2.white100};
  padding: 4px;
  box-shadow: -10px 0px 20px 0px #30427814, -2px 0px 6px 0px #3042780f;
  border-radius: 16px 0 0 16px;
  position: relative;
  z-index: 1;
  border: 1px solid transparent;
  transition: all 300ms ease-out;

  &:hover {
    border-color: ${({ $color }) => `${$color} transparent ${$color} ${$color}`};
    box-shadow: ${({ $color }) =>
      `-14px 0px 28px 0px ${hext2Rgba($color, 0.1)},
      -6px 0px 12px 0px ${hext2Rgba($color, 0.1)}`};
  }
`;

const CTACardLayout = styled.div<{ $bg: string }>`
  position: relative;
  box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.04),
    0px 0px 1px rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  width: 84px;
  height: 60px;
  background-color: ${({ $bg }) => $bg || colorsV2.white100};
  justify-items: center;
  text-align: center;
  z-index: 1;
  transition: all 300ms;
`;

const Title = ({ children }) => {
  return (
    <Typography.Text variant="medium/14-20" color={colorsV2.white100}>
      {children}
    </Typography.Text>
  );
};

const SubTitle = ({ children }) => {
  return (
    <Typography.Text variant="semi-bold/14-20" color={colorsV2.neutral140}>
      {children}
    </Typography.Text>
  );
};

const Score = ({ children }) => {
  return (
    <Typography.Text variant="bold/14-20" color={colorsV2.primary100}>
      {children}
    </Typography.Text>
  );
};

interface FABLandingHomeProps {
  topHeight?: number;
}

export const FABLandingHome = (props: FABLandingHomeProps) => {
  const { topHeight = 50 } = props;
  return (
    <Container $topHeight={topHeight}>
      <CtaCardWrapper
        initial="hidden"
        whileHover="visible"
        animate="hidden"
        variants={tagWrapperVariants}
        $color={colorsV2.green100}
      >
        <AnimationWrapper>
          <Tag1 variants={tag1Variants}>
            <SubTitle>IELTS</SubTitle>
            <Score>7.5</Score>
          </Tag1>
          <Tag3 variants={tag3Variants}>
            <Rating />
          </Tag3>
          <Tag2 variants={tag2Variants}>
            <SubTitle>IELTS</SubTitle>
            <Score>8.5</Score>
          </Tag2>
        </AnimationWrapper>
        <NewTabLink href={`${environment.REACT_APP_DOL_CORP_WEBSITE_HOST_NAME}/hall-of-fame`}>
          <CTACardLayout $bg={colorsV2.green100}>
            <Title>Điểm & Review</Title>
          </CTACardLayout>
        </NewTabLink>
      </CtaCardWrapper>

      <CtaCardWrapper
        initial="hidden"
        whileHover="visible"
        animate="hidden"
        variants={certWrapperVariants}
        $color={colorsV2.primary100}
      >
        <AnimationWrapper>
          <CertId variants={certIdVariants}>
            <NationId />
            <LazyImage
              className="cert-image"
              src={getAssetLinkByHandle({ handle: 'cm9woa1lnbe610714upbxmadx' })}
            />
          </CertId>
          <CertUs variants={certUsVariants}>
            <NationUs />
            <LazyImage
              className="cert-image"
              src={getAssetLinkByHandle({ handle: 'cm9woapctbcr70711zyhs9v7s' })}
            />
          </CertUs>
          <CertVn variants={certVnVariants}>
            <NationVn />
            <LazyImage
              className="cert-image"
              src={getAssetLinkByHandle({ handle: 'cm9wob97xbcy10711v3vzabqk' })}
            />
          </CertVn>
        </AnimationWrapper>
        <NewTabLink
          href={`${environment.REACT_APP_DOL_CORP_WEBSITE_HOST_NAME}/gioi-thieu-linearthinking`}
        >
          <CTACardLayout $bg={colorsV2.primary100}>
            <Title>Linear-thinking</Title>
          </CTACardLayout>
        </NewTabLink>
      </CtaCardWrapper>

      <CtaCardWrapper
        initial="hidden"
        whileHover="visible"
        animate="hidden"
        variants={bookWrapperVariants}
        $color={colorsV2.purple100}
      >
        <AnimationWrapper>
          <Book3 variants={book3Variants}>
            <LazyImage
              className="book-image"
              src={getAssetLinkByHandle({ handle: 'cm9qhp5wmt7u5071137aq8i8y' })}
            />
          </Book3>
          <Book2 variants={book2Variants}>
            <LazyImage
              className="book-image"
              src={getAssetLinkByHandle({ handle: 'cm9qhp5wmt7u70711cxjqz1w5' })}
            />
          </Book2>
          <Book1 variants={book1Variants}>
            <LazyImage
              className="book-image"
              src={getAssetLinkByHandle({ handle: 'cm9qhp5uvt9120h0ydhw1hynw' })}
            />
          </Book1>
        </AnimationWrapper>
        <NewTabLink href={`${environment.REACT_APP_DOL_CORP_WEBSITE_HOST_NAME}/book`}>
          <CTACardLayout $bg={colorsV2.purple100}>
            <Title>Sách độc quyền</Title>
          </CTACardLayout>
        </NewTabLink>
      </CtaCardWrapper>
    </Container>
  );
};
